import React, { useState } from 'react';
import {
    NativeSyntheticEvent,
    TextInputKeyPressEventData,
} from 'react-native';

import { translate } from '../../../services/translate';
import { crmService } from '../../../services/central-api/crm';
import ActMaintenanceRequestModal from './ActMaintenanceRequestModal';
import MaintenanceRequestModal from './MaintenanceRequestModal';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { CRMParamList } from '../../../typings/Params';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Kanban from '../../../components/Kanban';
import { useQuery } from '@tanstack/react-query';

export default function MaintenanceRequests() {
    const navigation = useNavigation<NavigationProp<CRMParamList, 'MaintenanceRequests'>>();
    const route = useRoute<RouteProp<CRMParamList, 'MaintenanceRequests'>>();
    const [filters, setFilters] = useFilters<CRMParamList['MaintenanceRequests']>({ ...route.params });

    const [textFilter, setTextFilter] = useState('');

    const [selectedEventId, setSelectedEventId] = useState(0);
    const [isCameraHistoryModalVisible, setIsCameraHistoryModalVisible] = useState(false);
    const [isActMaintenanceRequestModalVisible, setIsActMaintenanceRequestModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const { data: maintenanceRequests, refetch } = useQuery({
        queryKey: ['getMaintenanceRequests', filters],
        queryFn: () => crmService.getMaintenanceRequests(filters),
        enabled: isFocused,
        refetchInterval: isFocused ? 30000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
            }));
        }
    }

    if (!maintenanceRequests) {
        return <></>;
    }

    return (
        <Container>
            <Header
                title={translate('maintenanceRequests')}
                actions={
                    <FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                        style={{ flex: 1 }}
                    />
                }
            />
            <Kanban
                columns={[{
                    key: 'pending',
                    label: translate('pending'),
                }, {
                    key: 'ongoing',
                    label: translate('ongoing'),
                }, {
                    key: 'concluded',
                    label: translate('concluded'),
                }]}
                cards={maintenanceRequests.map(mr => {
                    let column = 'pending';
                    if (!mr.finish && mr.responsibleUser) {
                        column = 'ongoing';
                    } else if (mr.finish) {
                        column = 'concluded';
                    }
                    return {
                        column,
                        content: mr,
                        key: String(mr.id),
                    };
                })}
                cardContent={(item) => ({
                    id: item.content.id,
                    start: item.content.start,
                    finish: item.content.finish,
                    title: translate('maintenanceRequest'),
                    responsible: item.content.responsibleUser,
                    additionalInfo: [item.content.camera.serialNo ? item.content.camera.serialNo : item.content.camera.title],
                    priority: item.content.priority ? 'critical' : undefined,
                })}
                onClickCard={({ content: item }) => {
                    setSelectedEventId(item.id);

                    if (!item.finish && !item.responsibleUser) {
                        return setIsActMaintenanceRequestModalVisible(true);
                    }
                    setIsCameraHistoryModalVisible(true);
                }}
            />

            <ActMaintenanceRequestModal
                maintenanceRequestId={selectedEventId}
                isVisible={isActMaintenanceRequestModalVisible}
                setIsVisible={setIsActMaintenanceRequestModalVisible}
                onSubmit={() => {
                    refetch();
                }}
            />
            <MaintenanceRequestModal
                maintenanceRequestId={selectedEventId}
                isVisible={isCameraHistoryModalVisible}
                setIsVisible={setIsCameraHistoryModalVisible}
                onSubmit={() => {
                    refetch();
                }}
            />

        </Container>
    );
}
